import { auth } from './firebase';

// 本番環境では直接APIのURLを使用、開発環境ではプロキシを使用
const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiClient<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const user = auth.currentUser;
  if (!user) {
    throw new ApiError(401, '認証が必要です');
  }

  const token = await user.getIdToken();

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.error || 'Request failed');
  }

  return response.json();
}

// API エンドポイント
export const api = {
  // Auth
  getMe: () => apiClient<{ user: any }>('/auth/me'),

  // Users
  updateUserProfile: (data: { displayName?: string; photoUrl?: string | null }) =>
    apiClient<{ success: boolean; displayName: string; photoUrl: string | null }>('/users/me', { method: 'PUT', body: data }),

  // Payments
  getPayments: (params?: { yearMonth?: string; categoryId?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.yearMonth) searchParams.set('yearMonth', params.yearMonth);
    if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
    const query = searchParams.toString();
    return apiClient<{ payments: any[] }>(`/payments${query ? `?${query}` : ''}`);
  },
  createPayment: (data: any) =>
    apiClient<{ id: string }>('/payments', { method: 'POST', body: data }),
  updatePayment: (id: string, data: any) =>
    apiClient<{ id: string }>(`/payments/${id}`, { method: 'PUT', body: data }),
  deletePayment: (id: string) =>
    apiClient<{ success: boolean }>(`/payments/${id}`, { method: 'DELETE' }),

  // Categories
  getCategories: () => apiClient<{ categories: any[] }>('/categories'),
  createCategory: (data: any) =>
    apiClient<{ id: string }>('/categories', { method: 'POST', body: data }),
  updateCategory: (id: string, data: any) =>
    apiClient<{ id: string }>(`/categories/${id}`, { method: 'PUT', body: data }),
  deleteCategory: (id: string) =>
    apiClient<{ success: boolean }>(`/categories/${id}`, { method: 'DELETE' }),

  // Budgets
  getBudgets: (yearMonth: string) =>
    apiClient<{ budgets: any[] }>(`/budgets?yearMonth=${yearMonth}`),
  getBudgetSummary: (yearMonth: string) =>
    apiClient<{ summary: any[] }>(`/budgets/summary?yearMonth=${yearMonth}`),
  upsertBudget: (data: any) =>
    apiClient<{ id: string }>('/budgets', { method: 'POST', body: data }),

  // Settlements
  getSettlementSummary: (yearMonth?: string) => {
    const params = yearMonth ? `?yearMonth=${yearMonth}` : '';
    return apiClient<any>(`/settlements/summary${params}`);
  },
  getSettlementPreview: (paymentIds: string[]) =>
    apiClient<any>(`/settlements/preview?paymentIds=${paymentIds.join(',')}`),
  createSettlement: (data: any) =>
    apiClient<{ id: string }>('/settlements', { method: 'POST', body: data }),
  getSettlementHistory: () => apiClient<{ settlements: any[] }>('/settlements'),
  updateSettlementName: (id: string, name: string) =>
    apiClient<{ success: boolean; name: string }>(`/settlements/${id}`, { method: 'PUT', body: { name } }),
  getSettlementDetails: (id: string) => apiClient<{ settlement: any; payments: any[] }>(`/settlements/${id}`),

  // Recurring Payments
  getRecurringPayments: () => apiClient<{ recurringPayments: any[] }>('/recurring'),
  createRecurringPayment: (data: any) =>
    apiClient<{ id: string }>('/recurring', { method: 'POST', body: data }),
  updateRecurringPayment: (id: string, data: any) =>
    apiClient<{ id: string }>(`/recurring/${id}`, { method: 'PUT', body: data }),
  deleteRecurringPayment: (id: string) =>
    apiClient<{ success: boolean }>(`/recurring/${id}`, { method: 'DELETE' }),

  // Families
  createFamily: (data: { name: string }) =>
    apiClient<{ id: string; inviteCode: string }>('/families', { method: 'POST', body: data }),
  joinFamily: (data: { inviteCode: string }) =>
    apiClient<{ familyId: string }>('/families/join', { method: 'POST', body: data }),
  getInviteInfo: (inviteCode: string) =>
    apiClient<{ id: string; name: string; memberCount: number }>(`/families/invite/${inviteCode}`),
  getFamily: () => apiClient<{ family: any; members: any[] }>('/families'),
  getMyGroups: () => apiClient<{
    groups: Array<{ id: string; name: string; invite_code: string; role: string; member_count: number }>;
    activeFamilyId: string | null;
    isPremium: boolean;
    groupLimit: number;
    canCreateMore: boolean;
  }>('/families/my-groups'),
  switchGroup: (familyId: string) =>
    apiClient<{ success: boolean; family: { id: string; name: string; inviteCode: string } }>(`/families/switch/${familyId}`, { method: 'PUT' }),
  updateFamily: (data: { name: string }) =>
    apiClient<{ success: boolean }>('/families', { method: 'PUT', body: data }),
  refreshInviteCode: () =>
    apiClient<{ inviteCode: string }>('/families/invite-code', { method: 'POST' }),
  removeFamilyMember: (userId: string) =>
    apiClient<{ success: boolean }>(`/families/members/${userId}`, { method: 'DELETE' }),
  leaveFamily: () =>
    apiClient<{ success: boolean }>('/families/leave', { method: 'POST' }),
  deleteFamily: () =>
    apiClient<{ success: boolean }>('/families', { method: 'DELETE' }),

  // Notifications
  getNotifications: (params?: { limit?: number; offset?: number; unreadOnly?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    if (params?.unreadOnly) searchParams.set('unreadOnly', 'true');
    const query = searchParams.toString();
    return apiClient<{ notifications: any[]; unreadCount: number }>(
      `/notifications${query ? `?${query}` : ''}`
    );
  },
  markNotificationRead: (id: string) =>
    apiClient<{ success: boolean }>(`/notifications/${id}/read`, { method: 'PUT' }),
  markAllNotificationsRead: () =>
    apiClient<{ success: boolean }>('/notifications/read-all', { method: 'PUT' }),
  subscribePush: (data: { fcmToken: string; deviceInfo?: string }) =>
    apiClient<{ id: string }>('/notifications/subscribe', { method: 'POST', body: data }),
  unsubscribePush: (data: { fcmToken: string }) =>
    apiClient<{ success: boolean }>('/notifications/unsubscribe', { method: 'DELETE', body: data }),
};
